import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { Title, useNotify } from 'react-admin';
import authInstance from '../services/httpClient';
import { useAdminTheme } from '../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../theme/grounds';
import Logger from '../utils/Logger';
import { usePermissions } from '../context/PermissionContext';
import TabletHealth from '../components/TabletHealth';

/**
 * Order Manager Tablets — the admin side of the order tablets in the partner
 * stores. Laid out as the reskin prototype draws it: Tablet Notifications, a
 * composer on the left and the history beside it, the way Store Messages
 * pairs its push panel with Message History. Same cards, same section
 * heading, so the two pages read as one family.
 *
 * Owners and admins also get Tablet health above it: Esper's view of each tablet
 * (battery, Wi-Fi, app version...) with Refresh and Reboot.
 *
 * API: GET/POST /admin/tablet-notifications, GET /admin/tablet-notifications/reach,
 * GET /admin/tablet-health (+ POST /:deviceId/refresh|reboot).
 */

interface TabletNotification {
  id: string;
  title: string;
  message: string;
  recipientCount: number;
  seenCount: number;
  createdAt: string;
  sender: { id: string; email: string; firstName?: string | null; lastName?: string | null } | null;
}

const TITLE_MAX = 100;
const MESSAGE_MAX = 2000;

/** Stroke glyphs, 24-box, 1.9px — the prototype's own for this page. */
const GLYPH = {
  tablet: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M11 17.5h2" /></>,
  bell: <><path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5s1.5-1.5 1.5-5.5z" /><path d="M10.2 19a2 2 0 0 0 3.6 0" /></>,
  history: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.4V12l3 2" /></>,
  send: <><path d="M4 12l16-8-5 16-3-6z" /><path d="M12 14l8-10" /></>,
};

const Glyph: React.FC<{ name: keyof typeof GLYPH; size?: number }> = ({ name, size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {GLYPH[name]}
  </svg>
);

const senderName = (n: TabletNotification) => {
  const s = n.sender;
  if (!s) return 'Unknown';
  return [s.firstName, s.lastName].filter(Boolean).join(' ') || s.email;
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });

/** The prototype's .sm-card: a sunk header band with an ember glyph, body below. */
const PanelCard: React.FC<{ glyph: keyof typeof GLYPH; title: string; children: React.ReactNode }> = ({ glyph, title, children }) => {
  const { colors, cardStyle } = useAdminTheme();
  return (
    <Paper elevation={0} sx={{ ...cardStyle, p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 2, py: '13px', bgcolor: 'var(--sunk)', borderBottom: `1px solid ${colors.border}` }}
      >
        <Box sx={{ color: colors.info, display: 'grid', placeItems: 'center' }}><Glyph name={glyph} size={16} /></Box>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 650, fontSize: '0.84rem', color: colors.textPrimary, lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Stack>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
};

const OrderManagerTablets: React.FC = () => {
  const { colors, glassHeaderStyle, elevatedInputStyle } = useAdminTheme();
  const notify = useNotify();
  const { role } = usePermissions();
  // Staff reach a page only through its tick (PageGate), so a ticked page is theirs to use
  const isOwnerOrAdmin = role === 'owner' || role === 'admin' || role === 'staff';

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [reach, setReach] = useState<number | null>(null);
  const [history, setHistory] = useState<TabletNotification[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [reachError, setReachError] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await authInstance.get('/admin/tablet-notifications?limit=20');
      setHistory(data.notifications ?? []);
      setHistoryError(null);
    } catch (err: any) {
      Logger.error('[OrderManagerTablets] history', err);
      // A 404 means the API this page talks to is not deployed on this
      // environment yet; say so in the card rather than as a toast on every visit.
      setHistoryError(
        err?.response?.status === 404
          ? 'The tablet-notifications API is not deployed on this environment yet.'
          : err?.response?.data?.message || err?.message || 'Failed to load notification history',
      );
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchReach = useCallback(async () => {
    try {
      const { data } = await authInstance.get('/admin/tablet-notifications/reach');
      setReach(typeof data.tablets === 'number' ? data.tablets : null);
      setReachError(false);
    } catch (err) {
      Logger.error('[OrderManagerTablets] reach', err);
      setReach(null);
      setReachError(true);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchReach();
    // Tablets come and go; keep the reach honest while the composer is open.
    const t = setInterval(fetchReach, 30_000);
    return () => clearInterval(t);
  }, [fetchHistory, fetchReach]);

  const handleSend = async () => {
    if (sending) return;
    if (!title.trim() || !message.trim()) {
      notify('A title and a message are both needed', { type: 'warning' });
      return;
    }
    const tablets = reach ?? 0;
    const confirmed = window.confirm(
      `Send "${title.trim()}" to ${tablets} connected tablet${tablets === 1 ? '' : 's'}?\n\n` +
        "It lands in every store tablet's notification bell.",
    );
    if (!confirmed) return;

    setSending(true);
    try {
      const { data } = await authInstance.post('/admin/tablet-notifications', {
        title: title.trim(),
        message: message.trim(),
      });
      const n: TabletNotification = data;
      notify(`Sent to ${n.recipientCount} tablet${n.recipientCount === 1 ? '' : 's'}`, { type: 'success' });
      setTitle('');
      setMessage('');
      setHistory((cur) => [n, ...cur]);
    } catch (err: any) {
      Logger.error('[OrderManagerTablets] send', err);
      notify(err?.response?.data?.message || 'Failed to send tablet notification', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Vaperite Order Tablets" />

      <Box sx={{ ...glassHeaderStyle, px: 2.75, py: 2 }}>
        <Typography
          component="h1"
          sx={{ fontFamily: FONT_DISPLAY, fontStretch: '118%', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1, color: colors.textPrimary, m: 0 }}
        >
          Vaperite Order Tablets
        </Typography>
      </Box>

      <Box sx={{ px: 2.75, pb: 8 }}>
        {isOwnerOrAdmin && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.1}
              sx={{ mt: 3, mb: 1.5, fontFamily: FONT_DATA, fontSize: '0.69rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5FA8FF' }}
            >
              <Glyph name="tablet" />
              <span>Tablet Health</span>
            </Stack>
            <TabletHealth />
          </>
        )}

        {/* Section heading — the prototype's .sm-sec */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.1}
          sx={{ mt: 3, mb: 1.5, fontFamily: FONT_DATA, fontSize: '0.69rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5FA8FF' }}
        >
          <Glyph name="tablet" />
          <span>Tablet Notifications</span>
        </Stack>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, lg: 6 }}>
            <PanelCard glyph="bell" title="Send a Tablet Notification">
              <TextField
                fullWidth
                placeholder="Notification Title"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                size="small"
                sx={{ mb: 1, ...elevatedInputStyle }}
                slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Notification Message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                size="small"
                sx={{ mb: 0.75, ...elevatedInputStyle }}
                slotProps={{ htmlInput: { maxLength: MESSAGE_MAX } }}
              />
              <Typography sx={{ fontSize: '0.66rem', color: colors.textMuted, mb: 1.75 }}>
                Lands in the notification bell on every store tablet.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                {reachError ? (
                  <>Tablet count unavailable right now.</>
                ) : reach === null ? (
                  <>Checking how many tablets are connected…</>
                ) : (
                  <>This will reach <b>{reach} tablet{reach === 1 ? '' : 's'}</b> across every partner store.</>
                )}
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSend}
                disabled={sending}
                startIcon={sending ? <CircularProgress size={15} color="inherit" /> : <Glyph name="send" />}
                // The prototype's .sm-btn.red: brand red on every ground, not the ground's ember.
                sx={{ fontWeight: 650, py: 1.25, bgcolor: '#FF002C', color: '#fff', '&:hover': { bgcolor: '#e00027' } }}
              >
                {sending ? 'Sending…' : 'Send to Tablets'}
              </Button>
            </PanelCard>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <PanelCard glyph="history" title="Notification History">
              {loadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
              ) : historyError ? (
                <Typography variant="body2" sx={{ color: colors.textMuted, py: 3, textAlign: 'center' }}>
                  {historyError}
                </Typography>
              ) : history.length === 0 ? (
                <Typography variant="body2" sx={{ color: colors.textMuted, py: 3, textAlign: 'center' }}>
                  Nothing sent yet.
                </Typography>
              ) : (
                <Box>
                  {history.map((n, i) => {
                    const allSeen = n.recipientCount > 0 && n.seenCount >= n.recipientCount;
                    return (
                      <Box
                        key={n.id}
                        sx={{
                          py: 1.5,
                          pt: i === 0 ? 0 : 1.5,
                          borderBottom: i === history.length - 1 ? 0 : `1px solid ${colors.border}`,
                        }}
                      >
                        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: colors.textPrimary }}>{n.title}</Typography>
                          <Typography sx={{ fontFamily: FONT_DATA, fontSize: '0.6875rem', color: colors.textMuted, whiteSpace: 'nowrap' }}>
                            {fmtDate(n.createdAt)}&nbsp;&nbsp;{fmtTime(n.createdAt)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.5, fontSize: '0.78rem', lineHeight: 1.55, color: colors.textSecondary }}>{n.message}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.9, fontSize: '0.6875rem', color: colors.textMuted }}>
                          <span>Sent by {senderName(n)}</span>
                          <Box component="i" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textMuted, display: 'block' }} />
                          <span>{n.recipientCount} tablet{n.recipientCount === 1 ? '' : 's'}</span>
                          <Box component="i" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textMuted, display: 'block' }} />
                          <Box component="span" sx={{ color: allSeen ? colors.success : colors.warning }}>
                            {n.seenCount} of {n.recipientCount} seen
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </PanelCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OrderManagerTablets;
